import React, { useState, useRef, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronUp,
  faBars,
  faBuilding,
  faFileContract,
  faGlobe,
  faEdit,
  faSyncAlt,
  faSignOutAlt,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import {
  LuLayoutDashboard,
  LuFileText,
  LuShoppingCart,
  LuSearch,
  LuBookOpen,
  LuCreditCard,
  LuWallet,
  LuFileStack,
  LuStore,
  LuUsers,
  LuBuilding2,
  LuSettings,
  LuSmartphone,
  LuChartBar
} from "react-icons/lu";
import { useTranslation } from "react-i18next";
import { useAppContext } from '../../AppContext';
import { Dialog } from "primereact/dialog";
import UploadInvoice from '../Invoice/UploadInvoice';
import UploadPayment from '../Invoice/UploadPayment';
import { useHistory, useLocation, Link } from 'react-router-dom';
import useClickOutside from '../../utils/useClickOutside';
import UseScreenSize from '../../utils/UseSizeScreen';
import { openBankingVisibility } from '../OpenBanking/OpenBankingUtils';
import { subscriptionStatus } from '../../services/data';
import moment from 'moment';
import { logout } from '../../services/mainAxios';
import SubscriptionModal from './modals/SubscriptionModal';
import ChangeCompanyModal from './modals/ChangeCompanyModal';

const MenuItem = ({ item, expanded, onNavigate, isActive }) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasChildren = item.items && item.items.length > 0;
  const visibleChildren = item.items?.filter(child => child.visible !== false) || [];
  const hasSubmenu = hasChildren && visibleChildren.length > 0;

  const handleClick = () => {
    if (hasSubmenu) {
      setIsOpen(!isOpen);
    } else if (item.command) {
      item.command();
      if (onNavigate) onNavigate();
    } else if (item.url) {
      window.open(item.url, '_blank');
    }
  };

  if (item.visible === false) return null;

  return (
    <div className={`modern-sidebar__menu-item ${hasSubmenu ? 'modern-sidebar__menu-item--has-submenu' : ''}`}>
      <button
        className={`modern-sidebar__item ${isActive ? 'modern-sidebar__item--active' : ''}`}
        onClick={handleClick}
      >
        <span className="modern-sidebar__item-icon">
          {typeof item.icon === 'function' ? item.icon() : item.icon}
        </span>
        <span className="modern-sidebar__item-text">{item.label}</span>
        {hasSubmenu && (
          <span className={`modern-sidebar__item-arrow ${isOpen ? 'modern-sidebar__item-arrow--open' : ''}`}>
            <FontAwesomeIcon icon={faChevronDown} />
          </span>
        )}
        {!hasSubmenu && <span className="modern-sidebar__tooltip">{item.label}</span>}
      </button>

      {hasSubmenu && (
        <>
          {/* Inline submenu for expanded sidebar */}
          <div className={`modern-sidebar__submenu ${isOpen && expanded ? 'modern-sidebar__submenu--open' : ''}`}>
            {visibleChildren.map((child, index) => (
              <button
                key={child.id || index}
                className={`modern-sidebar__submenu-item ${child.isActive ? 'modern-sidebar__submenu-item--active' : ''}`}
                onClick={() => {
                  if (child.command) {
                    child.command();
                    setIsOpen(false);
                    if (onNavigate) onNavigate();
                  } else if (child.url) {
                    window.open(child.url, '_blank');
                  }
                }}
                style={child.style}
              >
                <span className="modern-sidebar__submenu-text">{child.label}</span>
              </button>
            ))}
          </div>
          {/* Flyout submenu for collapsed sidebar (hover) */}
          <div className="modern-sidebar__flyout">
            <div className="modern-sidebar__flyout-label">{item.label}</div>
            {visibleChildren.map((child, index) => (
              <button
                key={child.id || index}
                className={`modern-sidebar__flyout-item ${child.isActive ? 'modern-sidebar__flyout-item--active' : ''}`}
                onClick={() => {
                  if (child.command) {
                    child.command();
                    if (onNavigate) onNavigate();
                  } else if (child.url) {
                    window.open(child.url, '_blank');
                  }
                }}
                style={child.style}
              >
                <span>{child.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const Sidebar = () => {
  const [openUploadInvoice, setOpenUploadInvoice] = useState(false);
  const [openUploadPayment, setOpenUploadPayment] = useState(false);
  const [appMenuOpen, setAppMenuOpen] = useState(false);
  const { t, i18n } = useTranslation("translations");
  const {
    user: [user, setUser],
    sideBarExpanded: [expanded, setExpanded],
    selectedCompany: [selectedCompany, setSelectedCompany],
    company: [fullCompany],
    saveLanguage,
    saveSidebar,
  } = useAppContext();

  const sidebarRef = useRef();
  const router = useHistory();
  const { width } = UseScreenSize();
  const routerLocation = useLocation();
  const location = routerLocation.pathname;
  const isMobile = width <= 1024;

  // Modal states for mobile
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);

  const handleClickOutside = () => {
    if (width <= 1024 && expanded?.show) {
      setExpanded({ show: false, hover: false });
    }
  };

  useClickOutside(sidebarRef, handleClickOutside);

  const onNavigate = () => {
    if (width <= 1024) {
      setExpanded({ show: false, hover: false });
    }
  };

  const visibility = useMemo(() => openBankingVisibility(fullCompany, user), [fullCompany, user]);

  // Subscription data
  const activeSubscription = useMemo(() => {
    return fullCompany?.companySubscriptionViews?.find(
      (sub) => sub.status === subscriptionStatus.active
    );
  }, [fullCompany?.companySubscriptionViews]);

  const upcomingSubscription = useMemo(() => {
    return fullCompany?.companySubscriptionViews?.find(
      (sub) => sub.status === subscriptionStatus.upcoming
    );
  }, [fullCompany?.companySubscriptionViews]);

  const daysRemaining = activeSubscription ? moment(activeSubscription.endDate).diff(moment(), 'days') : 0;

  const handleCompanySelect = (companyId) => {
    setSelectedCompany(companyId);
  };

  const handleLanguageToggle = () => {
    const newLang = i18n.language === 'alb' ? 'en' : 'alb';
    i18n.changeLanguage(newLang);
    localStorage.setItem("language", newLang);
    saveLanguage(newLang);
  };

  const handleLogout = () => {
    logout({ setUser, request: true });
    router.push("/");
  };

  const hardReloadPage = () => {
    window.location.reload(true);
  };

  const menuItems = [
    {
      visible: [1, 2, 3, 4, 5, 7].includes(user.role.id),
      label: t("navbar.dashboard"),
      command: () => router.push('/'),
      icon: <LuLayoutDashboard size={20} />,
      isActive: location === '/'
    },
    {
      visible: [1, 2, 3, 4, 5, 7].includes(user.role.id),
      label: t("navbar.fiscalizedInv"),
      command: () => router.push('/sale-invoices'),
      icon: <LuFileText size={20} />,
      isActive: location === '/sale-invoices'
    },
    {
      label: t("navbar.purchases"),
      command: () => router.push('/purchases-new'),
      visible: [1, 2, 4, 5, 6, 7, 8].includes(user.role.id) && user?.purchaseRole?.id !== 12,
      icon: <LuShoppingCart size={20} />,
      isActive: location === '/purchases-new'
    },
    {
      visible: [1, 2, 4, 5, 6, 7].includes(user.role.id),
      label: t("navbar.search"),
      icon: <LuSearch size={20} />,
      items: [
        {
          label: t("navbar.taxPayers"),
          command: () => router.push('/search-tax-payers'),
          isActive: location === '/search-tax-payers',
          visible: true
        },
        {
          label: t("navbar.invoices"),
          command: () => router.push('/search-invoices'),
          isActive: location === '/search-invoices',
          visible: true
        },
        {
          label: t("navbar.expCertificate"),
          command: () => router.push('/search-certificates'),
          isActive: location === '/search-certificates',
          visible: [1].includes(user.role.id)
        }
      ]
    },
    {
      visible: [1, 2, 4, 5, 6, 7].includes(user.role.id) || [8, 10, 11].includes(user?.purchaseRole?.id),
      label: t("navbar.myBooks"),
      icon: <LuBookOpen size={20} />,
      items: [
        {
          label: t("navbar.salesBook"),
          command: () => router.push('/books/sales'),
          isActive: location === '/books/sales',
          visible: [1, 2, 3, 4, 5, 6, 7, 8].includes(user.role.id)
        },
        {
          label: t("navbar.purchasesBook"),
          command: () => router.push('/books/purchases'),
          isActive: location === '/books/purchases',
          visible: [1, 2, 3, 5, 6, 8].includes(user?.role?.id) || [8, 13, 14].includes(user?.purchaseRole?.id)
        }
      ]
    },
    {
      visible: visibility.openBankAccounts || visibility.bankJournal,
      label: t("navbar.bankTransactions"),
      icon: <LuCreditCard size={20} />,
      items: [
        {
          label: t("company.bankAccounts"),
          command: () => router.push('/open-bank-accounts'),
          isActive: location === '/open-bank-accounts',
          visible: visibility.openBankAccounts
        },
        {
          label: "Bank Journal",
          command: () => router.push('/bank-journal'),
          isActive: location === '/bank-journal',
          visible: visibility.bankJournal
        }
      ]
    },
    {
      visible: visibility.paymentReciept || visibility.paymentOrder || visibility.payroll,
      label: t("navbar.payments"),
      icon: <LuWallet size={20} />,
      items: [
        {
          label: t("navbar.paymentReceipt"),
          command: () => router.push('/payments-receipt'),
          isActive: location === '/payments-receipt',
          visible: visibility.paymentReciept
        },
        {
          label: t("navbar.paymentOrder"),
          command: () => router.push('/payment-order'),
          isActive: location === '/payment-order',
          visible: visibility.paymentOrder
        },
        {
          label: t("payRoll.payRoll"),
          command: () => router.push('/pay-roll'),
          isActive: location === '/pay-roll',
          visible: visibility.payroll
        }
      ]
    },
    {
      visible: [1, 2, 4, 5, 7, 15, 16].includes(user.role.id),
      label: t("navbar.myInvoices"),
      icon: <LuFileStack size={20} />,
      items: [
        {
          label: t("navbar.myInvoices"),
          command: () => router.push('/my-invoices'),
          isActive: location === '/my-invoices',
          visible: [1, 2, 4, 5, 7, 15].includes(user.role.id)
        },
        {
          label: t("navbar.createInvoice"),
          command: () => router.push('/create-invoice'),
          isActive: location === '/create-invoice',
          visible: [1, 2, 4, 15].includes(user.role.id),
          style: { borderBottom: '1px solid #e5e7eb' }
        },
        {
          label: t("navbar.MyWTNs"),
          command: () => router.push('/view-wtn'),
          isActive: location === '/view-wtn',
          visible: [1, 2, 4, 5, 7, 16].includes(user.role.id)
        },
        {
          label: t("navbar.createWTN"),
          command: () => router.push('/create-wtn'),
          isActive: location === '/create-wtn',
          visible: [1, 2, 4, 5, 16].includes(user.role.id),
          style: { borderBottom: '1px solid #e5e7eb' }
        },
        {
          label: t("navbar.transportInv"),
          command: () => router.push('/view-transport-invoices'),
          isActive: location === '/view-transport-invoices',
          visible: [1, 2, 4, 5, 7, 16].includes(user.role.id)
        },
        {
          label: t("navbar.createTransportInv"),
          command: () => router.push('/create-transport-invoice'),
          isActive: location === '/create-transport-invoice',
          visible: [1, 2, 4, 5, 16].includes(user.role.id)
        }
      ]
    },
    {
      visible: [1, 2, 4, 5, 15].includes(user.role.id),
      label: "My POS",
      icon: <LuStore size={20} />,
      items: [
        {
          label: "My Pos",
          command: () => router.push('/pos-transactions/create'),
          isActive: location === '/pos-transactions/create',
          visible: [1, 2, 4, 5, 7, 15].includes(user.role.id)
        },
        {
          label: t("invoice.transactions"),
          command: () => router.push('/pos-transactions'),
          isActive: location === '/pos-transactions',
          visible: [1, 2, 4, 7, 5, 15].includes(user.role.id)
        },
        {
          label: t("navbar.cashRegister"),
          command: () => router.push('/cash-register'),
          isActive: location === '/cash-register',
          visible: [1, 2, 4, 5, 15].includes(user.role.id)
        }
      ]
    }
  ];

  // Administration section items
  const adminItems = [
    {
      visible: [1, 3].includes(user.role.id),
      label: t("navbar.users"),
      command: () => router.push('/users'),
      icon: <LuUsers size={20} />,
      isActive: location === '/users'
    },
    {
      visible: [1, 2, 3].includes(user.role.id),
      label: t("navbar.companies"),
      command: () => router.push('/companies'),
      icon: <LuBuilding2 size={20} />,
      isActive: location === '/companies'
    },
    {
      visible: [1, 2, 3, 4, 5, 7].includes(user.role.id),
      label: t("navbar.reports"),
      command: () => router.push('/reports'),
      icon: <LuChartBar size={20} />,
      isActive: location === '/reports'
    },
    {
      visible: [1, 2, 3, 4, 5, 6].includes(user.role.id),
      label: t("navbar.settings"),
      icon: <LuSettings size={20} />,
      command: () => router.push('/settings'),
      isActive: location.startsWith('/settings')
    }
  ];

  const isExpanded = expanded?.show || expanded?.hover;
  const hasAdminItems = adminItems.some(item => item.visible !== false);

  return (
    <>
      <Dialog
        header={t("invoice.upload")}
        visible={openUploadInvoice}
        onHide={() => setOpenUploadInvoice(false)}
        style={{ width: "26vw" }}
        breakpoints={{ "960px": "75vw", "641px": "100vw" }}
      >
        <UploadInvoice user={user} selectedCompany={selectedCompany} />
      </Dialog>

      <Dialog
        header={t("navbar.uploadPayment")}
        visible={openUploadPayment}
        onHide={() => setOpenUploadPayment(false)}
        style={{ width: "26vw" }}
        breakpoints={{ "960px": "75vw", "641px": "100vw" }}
      >
        <UploadPayment user={user} selectedCompany={selectedCompany} fullCompany={fullCompany} />
      </Dialog>

      {/* Mobile overlay */}
      {width <= 1024 && isExpanded && (
        <div
          className="sidebar-overlay sidebar-overlay--visible"
          onClick={() => setExpanded({ show: false, hover: false })}
        />
      )}

      <aside
        ref={sidebarRef}
        className={`modern-sidebar ${isExpanded ? 'modern-sidebar--expanded' : ''}`}
      >
        {/* Header with Logo */}
        <div className="modern-sidebar__header">
          <Link to="/" className="modern-sidebar__logo">
            {isExpanded ? (
              <img
                src="/Logo-Profisc-2.png"
                alt="ProFisc"
                className="modern-sidebar__logo-full"
              />
            ) : (
              <img
                src="/profisc-icon.png"
                alt="ProFisc"
                className="modern-sidebar__logo-icon"
              />
            )}
          </Link>
          {isExpanded && (
            <button
              className="modern-sidebar__toggle"
              onClick={() => {
                const next = { show: !expanded?.show, hover: false };
                setExpanded(next);
                saveSidebar(next);
              }}
            >
              <FontAwesomeIcon icon={faBars} />
            </button>
          )}
        </div>

        {/* Navigation Content */}
        <div className="modern-sidebar__content">
          {/* Main Menu Items */}
          {menuItems.map((item, index) => (
            <MenuItem
              key={item.id || index}
              item={item}
              expanded={isExpanded}
              onNavigate={onNavigate}
              isActive={item.isActive}
            />
          ))}

          {/* Administration Section */}
          {hasAdminItems && (
            <>
              <div className="modern-sidebar__section-title">
                {t("navbar.administration")}
              </div>
              {adminItems.map((item, index) => (
                <MenuItem
                  key={`admin-${index}`}
                  item={item}
                  expanded={isExpanded}
                  onNavigate={onNavigate}
                  isActive={item.isActive}
                />
              ))}
            </>
          )}
        </div>

        {/* Mobile Section - Company, Subscription, Help, Language, User, Logout */}
        {isMobile && (
          <div className="modern-sidebar__mobile-section">
            {/* Subscription */}
            <button
              className="modern-sidebar__mobile-item"
              onClick={() => setShowSubscriptionModal(true)}
            >
              <FontAwesomeIcon icon={faFileContract} className="modern-sidebar__mobile-item-icon" />
              <span className="modern-sidebar__mobile-item-text">
                {activeSubscription?.subscriptionName || t("subscription.subscription")}
              </span>
              {activeSubscription && (
                <span className={`modern-sidebar__mobile-badge ${daysRemaining < 10 ? 'modern-sidebar__mobile-badge--danger' : ''}`}>
                  {daysRemaining}d
                </span>
              )}
            </button>

            {/* Language Toggle */}
            <button
              className="modern-sidebar__mobile-item"
              onClick={handleLanguageToggle}
            >
              <FontAwesomeIcon icon={faGlobe} className="modern-sidebar__mobile-item-icon" />
              <span className="modern-sidebar__mobile-item-text">
                {i18n.language === 'alb' ? 'Shqip' : 'English'}
              </span>
              <FontAwesomeIcon icon={faCheck} className="modern-sidebar__mobile-item-check" />
            </button>

            {/* Divider */}
            <div className="modern-sidebar__mobile-divider" />

            {/* User Dropdown */}
            <div className="modern-sidebar__user-dropdown">
              <button
                className="modern-sidebar__user-card"
                onClick={() => setUserDropdownOpen(!userDropdownOpen)}
              >
                <div className="modern-sidebar__user-card-avatar">
                  {user?.username?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div className="modern-sidebar__user-card-info">
                  <span className="modern-sidebar__user-card-name">{user?.username}</span>
                  <span className="modern-sidebar__user-card-role">{user?.role?.description}</span>
                </div>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`modern-sidebar__user-card-arrow ${userDropdownOpen ? 'modern-sidebar__user-card-arrow--open' : ''}`}
                />
              </button>

              <div className={`modern-sidebar__user-dropdown-menu ${userDropdownOpen ? 'modern-sidebar__user-dropdown-menu--open' : ''}`}>
                {/* Edit Profile */}
                <Link
                  to={"/users/edit/" + user?.id}
                  className="modern-sidebar__mobile-item"
                  onClick={onNavigate}
                >
                  <FontAwesomeIcon icon={faEdit} className="modern-sidebar__mobile-item-icon" />
                  <span className="modern-sidebar__mobile-item-text">{t("navbar.editProfile")}</span>
                </Link>

                {/* Sync */}
                <button
                  className="modern-sidebar__mobile-item"
                  onClick={hardReloadPage}
                >
                  <FontAwesomeIcon icon={faSyncAlt} className="modern-sidebar__mobile-item-icon" />
                  <span className="modern-sidebar__mobile-item-text">{t("navbar.sync")}</span>
                </button>

                {/* Logout */}
                <button
                  className="modern-sidebar__mobile-item modern-sidebar__mobile-item--danger"
                  onClick={handleLogout}
                >
                  <FontAwesomeIcon icon={faSignOutAlt} className="modern-sidebar__mobile-item-icon" />
                  <span className="modern-sidebar__mobile-item-text">{t("navbar.logout")}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* App Store Dropdown at Bottom */}
        <div className="modern-sidebar__app-section">
          <div className="modern-sidebar__app-dropdown">
            <button
              className="modern-sidebar__app-btn"
              onClick={() => setAppMenuOpen(!appMenuOpen)}
            >
              <span className="modern-sidebar__app-btn-icon">
                <LuSmartphone size={20} />
              </span>
              {isExpanded && (
                <>
                  <span className="modern-sidebar__app-btn-text">{t('navbar.getApp', 'Get the App')}</span>
                  <FontAwesomeIcon
                    icon={appMenuOpen ? faChevronUp : faChevronDown}
                    className="modern-sidebar__app-btn-arrow"
                  />
                </>
              )}
              {!isExpanded && (
                <span className="modern-sidebar__tooltip">{t('navbar.getApp', 'Get the App')}</span>
              )}
            </button>
            {appMenuOpen && isExpanded && (
              <div className="modern-sidebar__app-menu">
                <a
                  href="https://play.google.com/store/apps/details?id=com.tetrapro.profiscpro"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="modern-sidebar__app-menu-item"
                >
                  <img src="/play_store_icon.png" alt="Google Play" className="modern-sidebar__app-menu-icon" />
                  <span>Google Play</span>
                </a>
                <a
                  href="https://apps.apple.com/app/profisc"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="modern-sidebar__app-menu-item"
                >
                  <img src="/apple_icon.png" alt="App Store" className="modern-sidebar__app-menu-icon" />
                  <span>App Store</span>
                </a>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Mobile Modals */}
      {isMobile && (
        <>
          <SubscriptionModal
            isOpen={showSubscriptionModal}
            onClose={() => setShowSubscriptionModal(false)}
            subscription={activeSubscription}
            upcomingSubscription={upcomingSubscription}
            t={t}
          />

          <ChangeCompanyModal
            isOpen={showCompanyModal}
            onClose={() => setShowCompanyModal(false)}
            companies={user?.companiesRights ?? []}
            selectedCompany={selectedCompany}
            onSelect={handleCompanySelect}
            t={t}
          />
        </>
      )}
    </>
  );
};

export default Sidebar;
